import { useEffect, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";

/**
 * A textarea that grows to fit its content — no internal scrollbar.
 * Use in places where the AI drafts variable-length answers that would
 * otherwise scroll inside a small fixed-height box.
 *
 * Props: everything `Textarea` accepts, plus:
 *   minRows   — starting height (default 3)
 *   maxHeight — pixel cap (default 640). If content exceeds this,
 *               a scrollbar appears inside the textarea rather than
 *               taking over the page.
 */
export default function AutoResizeTextarea({
  value,
  onChange,
  minRows = 3,
  maxHeight = 640,
  className = "",
  ...rest
}) {
  const ref = useRef(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    // Reset first so shrinking works too
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  // Resize on value change (covers AI-drafted content set programmatically).
  useEffect(() => { resize(); /* eslint-disable-next-line */ }, [value]);
  // Also resize on mount so long saved answers open at full height.
  useEffect(() => { resize(); /* eslint-disable-next-line */ }, []);

  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={(e) => { onChange && onChange(e); resize(); }}
      onInput={resize}
      rows={minRows}
      className={`resize-none ${className}`}
      {...rest}
    />
  );
}
